// frontend/src/components/Dashboard/RegimeIntelligence.jsx
//
// Regime Intelligence card: renders the latent-state market model from
// /api/regime-intel — current regime + probability, latent trend dynamics
// (velocity/acceleration), market quality, transition risk, and the raw
// hidden state. Replaces the old rule-based "Leaf Market Regime" card.
import React from 'react';
import { TrendingUp, TrendingDown, Minus, Activity, Waves, Timer, Gauge } from 'lucide-react';
import useRegimeIntel from '../../hooks/useRegimeIntel';
import InfoHint from '../../components/Common/InfoHint';

const REGIME_STYLES = {
  STRONG_BULL: { text: 'text-green-600 dark:text-green-400', bar: 'bg-green-500', dot: '🟢' },
  WEAK_BULL: { text: 'text-green-500 dark:text-green-300', bar: 'bg-green-400', dot: '🟢' },
  SIDEWAYS: { text: 'text-gray-500 dark:text-gray-400', bar: 'bg-gray-400', dot: '⚪' },
  WEAK_BEAR: { text: 'text-red-500 dark:text-red-300', bar: 'bg-red-400', dot: '🔴' },
  STRONG_BEAR: { text: 'text-red-600 dark:text-red-400', bar: 'bg-red-500', dot: '🔴' },
  UNKNOWN: { text: 'text-gray-400', bar: 'bg-gray-300', dot: '⚪' },
};

const LEVEL_COLORS = {
  LOW: 'text-green-600 dark:text-green-400',
  MEDIUM: 'text-yellow-600 dark:text-yellow-400',
  HIGH: 'text-red-600 dark:text-red-400',
};
// Persistence is good when HIGH — inverse of the noise/volatility palette.
const PERSISTENCE_COLORS = {
  LOW: 'text-red-600 dark:text-red-400',
  MEDIUM: 'text-yellow-600 dark:text-yellow-400',
  HIGH: 'text-green-600 dark:text-green-400',
};

const fmt = (value, digits = 2, suffix = '') =>
  value === null || value === undefined || Number.isNaN(value) ? '—' : `${value.toFixed(digits)}${suffix}`;

const RegimeIntelligence = ({ candleData, symbol, timeframe, enabled = true }) => {
  const { data, isCalculating, lastUpdate } = useRegimeIntel(candleData, { symbol, timeframe, enabled });

  if (!data) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 text-center text-gray-500 dark:text-gray-400 h-full flex items-center justify-center">
        {!enabled ? 'Regime Intelligence' : isCalculating ? 'Fitting regime model…' : 'Waiting for market data…'}
      </div>
    );
  }

  const { status, regime, trend, quality, transition, validation } = data;
  const isProxy = validation?.method?.startsWith('proxy');
  const style = REGIME_STYLES[regime.label] || REGIME_STYLES.UNKNOWN;
  const DirectionIcon = trend.direction === 'UP' ? TrendingUp : trend.direction === 'DOWN' ? TrendingDown : Minus;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm h-full flex flex-col">
      {/* Header — single compact row (wraps in the narrow xl rail) */}
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap items-center justify-between gap-x-2">
          <span className="flex items-start gap-1"><h3 className="text-base font-semibold text-gray-900 dark:text-white">
            Regime Intelligence
          </h3><InfoHint title="Regime Intelligence" align="left">A statistical model of the market state, fitted on history and graded out-of-sample. It estimates the current regime (bull / sideways / bear) with a probability, the hidden trend's slope and acceleration, and market-quality metrics. Honesty note: the % is confidence in the STATE, not the odds of the next move — audited OOS across 6 symbols: bars labeled ≥80% bull rose over the next 20 bars 57% of the time; ≥60% bear fell 72% of the time. The price forecast band is interval-calibrated (its 90% band covered 85–93% of outcomes).</InfoHint></span>
          <div className="flex items-center space-x-2">
            {isCalculating && (
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            )}
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {symbol} • {timeframe}
              {lastUpdate && ` • ${lastUpdate.toLocaleTimeString()}`}
            </span>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3 flex-1 flex flex-col">
        {status === 'insufficient_data' && (
          <div className="rounded-lg bg-gray-50 dark:bg-gray-700/50 p-4 text-sm text-gray-500 dark:text-gray-400">
            Not enough history to estimate the market regime (need at least 60 bars).
          </div>
        )}

        {status === 'degraded' && (
          <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 p-3 text-xs text-amber-700 dark:text-amber-300">
            Trend-only mode: not enough history to fit the regime model (needs ~300 bars).
            Direction comes from the latent trend; probabilities are unavailable.
          </div>
        )}

        {isProxy && (
          <div className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20 p-3 text-xs text-indigo-700 dark:text-indigo-300">
            {validation.fallback
              ? 'Proxy fallback: the statistical model fit failed for this series — regimes below come from rule-based moving-average classification instead.'
              : 'Rule-based proxy mode: under 550 bars of history — regimes come from moving-average classification, not the fitted statistical model.'}
          </div>
        )}

        {status !== 'insufficient_data' && (
          <>
            {/* One row at lg (12-col split 3/4/5) when the card is full width;
                vertical stack at xl where it lives in the narrow right rail.
                Stacks on phone, 2+full on md. */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-3 xl:flex xl:flex-col xl:flex-1 xl:justify-between">
            {/* Current Regime */}
            <div className="lg:col-span-3 xl:col-auto bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 text-center flex flex-col justify-center">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current Regime</div>
              <div className={`text-xl font-bold ${style.text}`}>
                {style.dot} {regime.label.replace('_', ' ')}
              </div>
              {regime.probability !== null && (
                <div className="mt-2">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-40 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${style.bar}`}
                        style={{ width: `${Math.round(regime.probability * 100)}%` }}
                      />
                    </div>
                    <span className={`text-sm font-medium ${style.text}`}>
                      {Math.round(regime.probability * 100)}%
                    </span>
                  </div>
                </div>
              )}
              {transition?.expectedDurationBars && (
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  expected to hold ~{transition.expectedDurationBars} bars
                </div>
              )}
            </div>

            {/* Trend */}
            <div className="lg:col-span-4 xl:col-auto bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 flex flex-col justify-center">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <DirectionIcon className={`w-4 h-4 ${style.text}`} />
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Trend</h4>
                </div>
                <span className={`text-sm font-medium ${style.text}`}>{trend.direction}</span>
              </div>
              {/* 3-across when wide; stacked in the narrow xl rail */}
              <div className="grid grid-cols-3 xl:grid-cols-1 gap-3 xl:gap-2 text-center">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Strength</div>
                  <div className="flex items-center space-x-2">
                    <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${style.bar}`}
                        style={{ width: `${Math.min(trend.strengthScore, 100)}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {trend.strengthScore}%
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Slope</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {fmt(trend.velocityPctPerBar, 3, ' %/bar')}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Acceleration</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {fmt(trend.accelerationPctPerBar2, 4)}
                  </div>
                  {trend.accelerationPctPerBar2 !== null && (
                    <div className={`text-xs ${trend.accelerationPctPerBar2 >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {trend.accelerationPctPerBar2 >= 0 ? 'accelerating' : 'weakening'}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Market Quality — Choppiness/ADX removed (redundant with
                Efficiency + the Trend section); 5 compact cards in one row so
                its height matches the Regime/Trend panels beside it. */}
            <div className="md:col-span-2 lg:col-span-5 xl:col-auto bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
              <span className="flex items-start gap-1 mb-2"><h4 className="text-sm font-medium text-gray-900 dark:text-white">Market Quality</h4><InfoHint title="Market quality" align="left">How tradeable this market is right now: Noise (one-step forecast error), Volatility (ATR), Persistence (chance the regime holds next bar), Efficiency (clean trend vs chop) and Hurst (trending memory vs mean-reversion vs random walk).</InfoHint></span>
              {/* flex-wrap + flex-1 so wrapped rows stretch to fill the width
                  (no empty cells on phone — the last 1-2 cards grow) */}
              <div className="flex flex-wrap gap-2">
                <QualityCard icon={Waves} label="Noise"
                  value={quality.noise ? quality.noise.level : '—'}
                  subtitle={quality.noise ? `${fmt(quality.noise.pct, 2, '%')} 1-step error` : null}
                  color={quality.noise ? LEVEL_COLORS[quality.noise.level] : ''} />
                <QualityCard icon={Activity} label="Volatility"
                  value={quality.volatility ? quality.volatility.level : '—'}
                  subtitle={quality.volatility ? `ATR ${fmt(quality.volatility.atrPct, 2, '%')}` : null}
                  color={quality.volatility ? LEVEL_COLORS[quality.volatility.level] : ''} />
                <QualityCard icon={Timer} label="Persistence"
                  value={quality.persistence ? quality.persistence.level : '—'}
                  subtitle={quality.persistence ? `${Math.round(quality.persistence.prob * 100)}% stay` : null}
                  color={quality.persistence ? PERSISTENCE_COLORS[quality.persistence.level] : ''} />
                <QualityCard icon={Gauge} label="Efficiency" {...interpretEfficiency(quality.efficiency)} />
                <QualityCard icon={Gauge} label="Hurst" {...interpretHurst(quality.hurst)} />
              </div>
            </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Plain-language interpretations of the quality metrics, so the card reads as a
// verdict ("CHOPPY") with the number and its textbook thresholds underneath.
const GOOD = 'text-green-600 dark:text-green-400';
const MID = 'text-yellow-600 dark:text-yellow-400';
const BAD = 'text-red-600 dark:text-red-400';
const NEUTRAL = 'text-gray-500 dark:text-gray-400';

const interpretEfficiency = (v) => {
  if (v === null || v === undefined) return { value: '—', subtitle: 'Kaufman ratio' };
  const word = v >= 0.5 ? 'CLEAN TREND' : v >= 0.3 ? 'MIXED' : 'CHOPPY';
  const color = v >= 0.5 ? GOOD : v >= 0.3 ? MID : BAD;
  return { value: word, color, subtitle: `${v.toFixed(2)} · >0.5 clean, <0.3 choppy` };
};

const interpretHurst = (v) => {
  if (v === null || v === undefined) return { value: '—', subtitle: 'memory estimate' };
  const word = v > 0.55 ? 'TRENDY MEMORY' : v < 0.45 ? 'MEAN-REVERTING' : 'RANDOM WALK';
  const color = v > 0.55 ? GOOD : v < 0.45 ? MID : NEUTRAL;
  return { value: word, color, subtitle: `${v.toFixed(2)} · ~0.5 random, >0.55 trendy` };
};

const QualityCard = ({ icon: Icon, label, value, subtitle, color = 'text-gray-900 dark:text-white' }) => (
  <div className="flex-1 min-w-[100px] bg-white dark:bg-gray-800 rounded-lg p-2">
    <div className="flex items-center space-x-1.5 mb-0.5">
      <Icon className="w-3.5 h-3.5 text-gray-400" />
      <span className="text-[11px] text-gray-500 dark:text-gray-400">{label}</span>
    </div>
    <div className={`text-xs font-bold ${color}`}>{value}</div>
    {subtitle && (
      <div className="text-[10px] text-gray-400 dark:text-gray-500">{subtitle}</div>
    )}
  </div>
);

export default RegimeIntelligence;
